import { ParsedQuery } from 'query-string'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'

export type Filter = {
  key: FilterParam
  property: string
  type?: 'string' | 'number' | 'boolean' | 'date' | 'array'
}

export type Transform<T> = (data: T) => {
  [key: string]: string | number | boolean | null | undefined | string[] | Date
}

export type FilterProps<T> = {
  filters: Filter[]
  data: T[]
  searchParams: ParsedQuery
  transform: Transform<T>
}

export const filterList = <T>({
  filters,
  data,
  searchParams,
  transform,
}: FilterProps<T>): T[] => {
  if (!filters.length || !Object.keys(searchParams).length) return data

  return data.filter((item) => {
    const object = transform(item)
    return filters.every((filter) => {
      const filterRawValue = searchParams[filter.key]
      const dataRawValue =
        filter.property in object ? object[filter.property] : undefined

      // Skip filters that don't have values
      if (filterRawValue === undefined || filterRawValue === null) {
        return true
      }

      const dataValue = makeArray(dataRawValue)
      const filterValue = makeArray(filterRawValue)

      if (filterValue.length === 0) {
        return true
      }

      if (dataValue.length === 0) {
        return false
      }

      if (filter.type === 'date') {
        return filterValue.some((filterVal) =>
          dataValue.some((dataVal) => filterDate(filterVal, dataVal)),
        )
      }

      return dataValue.some((dataVal) => filterValue.includes(dataVal))
    })
  })
}

const filterDate = (
  filterRawValue: string,
  dataRawValue: string | number | Date,
) => {
  const [begin, end] = filterRawValue.split('-')

  // If the format is invalid or missing parts, skip this filter
  if (!begin || !end || begin === '' || end === '') {
    return true
  }

  // Try to parse the dates, if parsing fails, skip this filter
  const filterBeginDate = parseFloat(begin)
  const filterEndDate = parseFloat(end)

  if (isNaN(filterBeginDate) || isNaN(filterEndDate)) {
    return true
  }

  // Create begin date at start of day (00:00:00.000)
  const beginDate = new Date(filterBeginDate)
  beginDate.setHours(0, 0, 0, 0)
  const filterBeginTime = beginDate.getTime()

  // Create end date at end of day (23:59:59.999)
  const endDate = new Date(filterEndDate)
  endDate.setHours(23, 59, 59, 999)
  const filterEndTime = endDate.getTime()

  // If the parsed dates are invalid, skip this filter
  if (isNaN(filterBeginTime) || isNaN(filterEndTime)) {
    return true
  }

  const dataDate = new Date(dataRawValue).getTime()

  // If the data date is invalid, exclude this item
  if (isNaN(dataDate)) {
    return false
  }

  return dataDate >= filterBeginTime && dataDate <= filterEndTime
}

const makeArray = (
  value:
    | string
    | string[]
    | number
    | number[]
    | boolean
    | boolean[]
    | Date
    | undefined
    | null,
) => {
  if (value === undefined || value === null) {
    return []
  }
  return Array.isArray(value)
    ? value
        .map((value) => value?.toString() || '')
        .filter((value) => value !== '')
    : [value?.toString() || '']
}
